import { SA_COLORS } from 'screens/support-shared/styled-comps';

/**
 * The queue dashboard's palette IS the support-agent inbox's palette — the two
 * screens sit beside each other and cross-link, so a second set of cream
 * values would drift the moment either moved. Everything below is an alias
 * onto SA_COLORS; only the composites at the end (row tint, scrim, shadows)
 * are new, because SA has no equivalent.
 *
 * One naming inversion to watch: this design calls the PAGE `BG_PAGE` and the
 * cards `BG_CARD`, and cards sit LIGHTER than the page. SA_COLORS names its
 * lightest value BG_PRIMARY, so the two are deliberately crossed here rather
 * than silently mismatched.
 */
export const QM = {
  TEXT_PRIMARY: SA_COLORS.TEXT_PRIMARY,
  TEXT_SECONDARY: SA_COLORS.TEXT_SECONDARY,
  TEXT_TERTIARY: SA_COLORS.TEXT_TERTIARY,

  BG_SUNKEN: SA_COLORS.BORDER_TERTIARY,
  BG_PAGE: SA_COLORS.BG_SECONDARY,
  BG_CARD: SA_COLORS.BG_PRIMARY,
  BG_WELL: SA_COLORS.BG_TERTIARY,

  BORDER_TERTIARY: SA_COLORS.BORDER_TERTIARY,
  BORDER_SECONDARY: SA_COLORS.BORDER_SECONDARY,
  BORDER_PRIMARY: SA_COLORS.BORDER_PRIMARY,
  BORDER_TERTIARY_SOFT: 'rgba(43,43,43,.10)',
  BORDER_INTERACTIVE: SA_COLORS.TEXT_PRIMARY,

  TEXT_ERROR: SA_COLORS.ERROR_700,
  TEXT_SUCCESS: SA_COLORS.SUCCESS_700,
  TEXT_BRAND: SA_COLORS.GOLD_700,
  TEXT_WARNING: SA_COLORS.WARNING_700,
  TEXT_INFO: SA_COLORS.INFO_700,

  BG_ERROR_1: SA_COLORS.ERROR_100,
  BG_ERROR_HEAVY: SA_COLORS.ERROR_200,
  BORDER_ERROR: SA_COLORS.ERROR_200,
  BG_WARNING_1: SA_COLORS.WARNING_100,
  BORDER_WARNING: SA_COLORS.WARNING_300,
  BG_SUCCESS_1: SA_COLORS.SUCCESS_100,
  BORDER_SUCCESS: SA_COLORS.SUCCESS_200,
  BG_GOLD_1: SA_COLORS.GOLD_100,
  BORDER_GOLD: SA_COLORS.GOLD_300,
  BG_INFO_1: SA_COLORS.INFO_100,
  BORDER_INFO: SA_COLORS.INFO_300,

  BG_INVERSE: SA_COLORS.TEXT_PRIMARY,
  TEXT_ON_INVERSE: SA_COLORS.BG_PRIMARY,

  // No SA equivalents — composites this screen introduces.
  ROW_HOT: 'rgba(255,219,219,.55)',
  SCRIM: 'rgba(43,43,43,.34)',
  SHADOW_MD: '0 2px 8px hsl(30 20% 15% / .10)',
  SHADOW_LG: '0 8px 24px hsl(30 20% 15% / .16)',
};

// An amber tint sits only a few steps from the cream ground, so the state
// pill leans on a thick left rail and a heavier weight to carry rank rather
// than on the surface colour. Without these two, reply-owed and a quiet row
// are hard to tell apart at a glance.
export const STATE_RAIL = '3px';
export const STATE_WEIGHT = 600;

export const REGIONS = [
  { value: null, label: 'All' },
  { value: 'US', label: 'US' },
  { value: 'IND', label: 'India' },
  { value: 'ROW', label: 'RoW' },
  { value: 'UNIDENTIFIED', label: 'Unidentified' },
];

export const PANES = [
  {
    key: 'bot',
    title: 'With the bot',
    showOwner: false,
    emptyTitle: 'Nothing with the bot',
    emptyNote: 'Every new chat has reached a human or resolved itself.',
  },
  {
    key: 'unassigned',
    title: 'Unassigned',
    showOwner: false,
    emptyTitle: 'Queue clear',
    emptyNote: 'Nothing is waiting for someone to pick it up.',
  },
  {
    key: 'agent',
    title: 'With an agent',
    showOwner: true,
    emptyTitle: 'No active chats',
    emptyNote: 'No ticket in this window currently has an owner.',
  },
];

// The view the list endpoint is asked for. chatbackend raises
// `ValueError(f"Unknown queue view: {view}")` on a mismatch, so these are
// load-bearing strings, not labels.
export const QUEUE_VIEW = {
  LIVE: 'live',
  RESOLVED: 'resolved',
};

// Which drill-down the modal is showing.
export const MODAL_KIND = {
  TOTAL: 'total',
  RESOLVED: 'resolved',
  PANE: 'pane',
};

// The pane whose tile goes red at non-zero. Must stay the key of the
// matching PANES entry or the tile never colours.
export const HOT_PANE = 'unassigned';

export const QUEUE_STATE = {
  REPLY_OWED: 'reply_owed',
  CUSTOMER_QUIET: 'customer_quiet',
  NO_INBOUND: 'no_inbound',
};

export const STATE_LABEL = {
  [QUEUE_STATE.REPLY_OWED]: 'REPLY OWED',
  [QUEUE_STATE.CUSTOMER_QUIET]: 'CUSTOMER TO REPLY',
  [QUEUE_STATE.NO_INBOUND]: 'NO CUSTOMER MESSAGE YET',
};

/**
 * Parking is one button to an agent but two opposite answers to "whose
 * court is this in", so the chip names the side. A single PARKED pill put
 * 194 rows blocked on the customer and 89 blocked on us behind the same
 * word, on the one screen built to tell them apart.
 *
 * Both keep the PARKED prefix: it marks a deliberate agent action rather
 * than a computed state, which is what separates `PARKED · CUSTOMER REPLY`
 * from the CUSTOMER TO REPLY pill it can sit beside.
 */
export const PARKED_ON = {
  CUSTOMER: 'customer',
  INTERNAL: 'internal',
};

export const PARKED_LABEL = {
  [PARKED_ON.CUSTOMER]: 'PARKED · CUSTOMER REPLY',
  [PARKED_ON.INTERNAL]: 'PARKED · INTERNAL TEAM',
};

// Past this, a reply-owed row stops being "waiting" and starts being a
// problem, so the pill escalates from amber to red. Only the colour moves:
// the wording used to change too, and said something untrue.
export const REPLY_OWED_CRITICAL_AFTER_SECONDS = 5 * 60;

export const RESOLUTION_BUCKETS = [
  { key: 'by_agent', label: 'BY AGENT' },
  { key: 'by_bot', label: 'BY BOT' },
  { key: 'abandoned', label: 'ABANDONED AT BOT' },
];

export const POLL_INTERVAL_MS = 30000;
// Three consecutive failures stop the loop. One dropped request is noise; a
// banner that flickers on every blip gets ignored. At a 30-second poll that
// is 90 seconds before the board admits it has stopped, so the chip carries
// the age of the data continuously rather than only once it gives up.
export const MAX_POLL_FAILURES = 3;

// Per-tile explainers, opened by the small "i" next to each tile title. The
// board's vocabulary is not self-evident — REPLY OWED, PARKED, ABANDONED AT
// BOT all mean something specific — and a supervisor will not go and read a
// doc. Each legend row renders as the real pill it describes, in the real
// tone, so an explanation cannot drift from the thing it explains.
export const INFO = {
  total: {
    title: 'Total live',
    body:
      'Created in this window and still open, whoever holds it. A ticket opened before the window is not counted anywhere on this board, however long it has been waiting — widen the window to bring it in. The caption below counts everything created in the window, resolved included, and the three state tiles beside this one always sum to the big number.',
    legend: [
      ['warn', 'REPLY OWED', 'customer spoke last, we have not answered'],
      ['well', 'CUSTOMER TO REPLY', 'we spoke last, the customer has not come back'],
      ['well', 'NO CUSTOMER MESSAGE YET', 'the customer has not said anything yet'],
    ],
  },
  bot: {
    title: 'With the bot',
    body:
      'The bot is handling these and no human is involved yet. Nobody is late in the sense the other tiles mean it, but a red row here is worth a look: the customer has been waiting on the bot long enough that it probably should have escalated. Rows group under the state they are in.',
    legend: [
      ['well', 'NO CUSTOMER MESSAGE YET', 'opened, customer has not spoken'],
      ['well', 'CUSTOMER TO REPLY', 'bot spoke last'],
      ['warn', 'REPLY OWED', 'customer spoke, bot has not answered'],
      ['errorHeavy', 'REPLY OWED', 'the same, past five minutes'],
    ],
  },
  unassigned: {
    title: 'Unassigned',
    body:
      'Escalated out of the bot and waiting for someone to pick up. Nobody is working these, so this is the tile that should draw your eye: it goes red the moment it is non-zero and stays neutral at nothing. Rows group under the state they are in, with the queue and disposition on the line beneath each one.',
    legend: [
      ['error', 'Tile red', 'non-zero — someone needs to pick up'],
      ['well', 'Tile neutral', 'zero — every escalation has an owner'],
      ['warn', 'REPLY OWED', 'customer is waiting on a reply'],
      ['errorHeavy', 'REPLY OWED', 'the same, past five minutes'],
    ],
  },
  agent: {
    title: 'With an agent',
    body:
      'Someone owns these. Sorted most-at-risk-first, so the top of the list is where a supervisor intervenes. Parked rows are common — roughly half — so they are deliberately the quietest thing here. Rows group under the state they are in, so each carries only its clock and badges; owner, queue and disposition sit on the line beneath.',
    legend: [
      ['warn', 'REPLY OWED', 'customer spoke last, agent has not answered'],
      ['errorHeavy', 'REPLY OWED', 'the same, past five minutes — the clock turns red'],
      ['info', 'SEEN', 'the agent read the customer and did not reply'],
      ['errorHeavy', 'REPLY OWED + SEEN', 'the combination to spot: read and ignored'],
      ['error', 'NEVER REPLIED', 'assigned but the agent has never sent anything'],
      ['well', 'PARKED · CUSTOMER REPLY', 'agent is waiting on the customer'],
      ['well', 'PARKED · INTERNAL TEAM', 'agent is blocked on another team'],
      ['well', 'CUSTOMER TO REPLY', 'agent replied, customer has not come back'],
    ],
  },
  resolved: {
    title: 'Resolved',
    body:
      'Closed inside the window — these have left the queue, so they are context rather than work. The three chips always sum to the total.',
    legend: [
      ['success', 'BY AGENT', 'an agent sent the closing message'],
      ['success', 'BY BOT', 'the bot closed it without a human'],
      ['well', 'ABANDONED AT BOT', 'the customer left while still with the bot'],
    ],
  },
  refresh: {
    title: 'Refresh',
    body: `The screen re-polls every ${POLL_INTERVAL_MS /
      1000} seconds. Filters, scroll position and loaded rows all survive a refresh, and numbers use tabular figures so digits never reflow. After ${MAX_POLL_FAILURES} consecutive failures the loop stops and a banner appears — the last good snapshot stays on screen, marked stale, rather than being replaced.`,
    legend: [
      ['success', 'Green dot', 'polling normally'],
      [
        'error',
        'Red dot + banner',
        `stopped after ${MAX_POLL_FAILURES} failures, data is stale`,
      ],
    ],
  },
};
